import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './HorizontalScrollContainer.css';

export interface HorizontalScrollContainerProps {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  style?: React.CSSProperties;
  showButtons?: boolean;
  ariaLabel?: string;
}

export const HorizontalScrollContainer: React.FC<HorizontalScrollContainerProps> = ({
  children,
  className = '',
  contentClassName = '',
  style,
  showButtons = true,
  ariaLabel = 'Conteúdo com scroll horizontal'
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const hasOverflow = scrollWidth > clientWidth + 1;
    setCanScrollLeft(hasOverflow && scrollLeft > 2);
    setCanScrollRight(hasOverflow && scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll();

    const handleScroll = () => {
      checkScroll();
    };

    el.addEventListener('scroll', handleScroll, { passive: true });

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        checkScroll();
      });
      resizeObserver.observe(el);
      if (el.firstElementChild) {
        resizeObserver.observe(el.firstElementChild);
      }
    }

    window.addEventListener('resize', checkScroll);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll, children]);

  const scrollByAmount = (amount: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className={`horizontal-scroll-wrapper ${className}`} style={style}>
      {canScrollLeft && (
        <div className="scroll-indicator indicator-left">
          {showButtons && (
            <button
              type="button"
              className="scroll-btn"
              onClick={() => scrollByAmount(-200)}
              aria-label="Deslocar conteúdo para a esquerda"
            >
              <ChevronLeft size={18} />
            </button>
          )}
        </div>
      )}

      <div
        ref={scrollRef}
        className={`horizontal-scroll-content ${contentClassName}`}
        tabIndex={0}
        role="region"
        aria-label={ariaLabel}
      >
        {children}
      </div>

      {canScrollRight && (
        <div className="scroll-indicator indicator-right">
          {showButtons && (
            <button
              type="button"
              className="scroll-btn"
              onClick={() => scrollByAmount(200)}
              aria-label="Deslocar conteúdo para a direita"
            >
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
